// The Radar D3 chart — an IMPERATIVE ISLAND, ported from js/renderers-radar.js
// (CHART_SKELETON_HTML / ensureChartFrame / updateRadarData + the clip-id + wedge-tooltip
// machinery). Same rationale as filter-sliders.ts: the SVG building + d3 join here is
// self-contained legacy drawing code, so it's ported into a TS module that RadarModule mounts
// into a React-owned (but React-childless) host, then drives from a useEffect. The pure
// geometry (angles, points, ranges, fractions, polygon points) lives in radar-view.ts and is
// unit-tested; this file is the DOM/D3 wiring (untested, like the other islands — no jsdom).
//
// Typed at its SEAMS (axes + entries in, fn params) per the 04h-4 typing call; only the d3
// .selectAll().data().join() internals stay loose. Ported once so Similarity (04j) can reuse
// the same drawing pipeline the legacy RadarKit exposed to it — the D3 does NOT stay on
// window.RadarKit (that's the hybrid we retired for the chip-bar).

import * as d3 from "d3";
import { formatMetricValue } from "../engines/stats-filter-kit.js";
import {
  CHART_SIZE,
  CHART_CENTER,
  CHART_RADIUS,
  CHART_CENTER_HOLE_RADIUS,
  AXIS_LABEL_MAX_LEN,
  RING_BAND_FRACS,
  TICK_FRACS,
  axisAngle,
  axisPoint,
  wedgePath,
  labelTransformPct,
  labelRotationDeg,
  truncateLabel,
  polygonPoints,
  isLowerBetter,
  type RadarAxis,
  type RadarAxisWithRange,
} from "./radar-view.js";

const SVG_NS = "http://www.w3.org/2000/svg";

// One player's shape on the chart. `fractions[i]`/`values[i]` are null for an axis this
// player has no value for (a cross-template merge, later) — the polygon is built only from
// the real points that exist, never a fabricated 0th-percentile vertex.
export interface RadarEntry {
  colorLetter: string;
  fractions: (number | null)[];
  values: (number | null | undefined)[];
  tooltipPrefix: string;
}

// The static skeleton: an SVG (clip def, ring/spoke/banded-fill/polygon/wedge groups, centre
// hole) plus a sibling HTML overlay for the axis + tick labels (HTML, not SVG <text>, so the
// viewBox scale never balloons their font — renderers-radar.js:302). Byte-faithful to
// CHART_SKELETON_HTML (renderers-radar.js:1103).
export const CHART_SKELETON_HTML = `
  <div class="radar-chart-inner">
    <svg class="radar-svg" viewBox="0 0 ${CHART_SIZE} ${CHART_SIZE}">
      <defs><clipPath class="radar-clip"><polygon class="radar-clip-polygon"></polygon></clipPath></defs>
      <g class="radar-rings"></g>
      <g class="radar-spokes"></g>
      <g class="radar-banded-fill"></g>
      <g class="radar-polygons"></g>
      <circle class="radar-center-hole" cx="${CHART_CENTER}" cy="${CHART_CENTER}" r="${CHART_CENTER_HOLE_RADIUS}"></circle>
      <g class="radar-wedges"></g>
    </svg>
    <div class="radar-labels-overlay"></div>
  </div>
`;

// clip-path url(#id) lookups need a real, unique id — two Radar modules open at once would
// otherwise both define a <clipPath> with the same id and every browser resolves url(#id) to
// the first one in the DOM, silently clipping the second module's fill to the first's shape.
let clipIdCounter = 0;
function ensureClipId(svg: SVGSVGElement): string {
  if (!svg.dataset.clipId) {
    svg.dataset.clipId = "radar-clip-" + ++clipIdCounter;
    svg.querySelector<SVGClipPathElement>(".radar-clip")!.id = svg.dataset.clipId;
  }
  return svg.dataset.clipId;
}

// Tick labels default muted and brighten only for the axis being hovered — the chart's own
// wedge hover calls this; the table→chart hover (04h-4c) will call the same function so both
// gestures highlight the same thing. renderers-radar.js:323.
export function setAxisTicksActive(svg: SVGSVGElement, axisKey: string, active: boolean): void {
  const overlay = svg.parentElement?.querySelector<HTMLElement>(".radar-labels-overlay");
  if (!overlay) return;
  Array.from(overlay.querySelectorAll<HTMLElement>(".radar-tick-label"))
    .filter((tick) => tick.dataset.axisKey === axisKey)
    .forEach((tick) => tick.classList.toggle("active", active));
}

// Builds the static frame (banded rings, spokes, wedges, axis labels + per-axis ticks). Depends
// only on the axis set, so it rebuilds only when that changes (a signature check), mirroring the
// legacy "rebuild skeleton only when the shape changes" convention. renderers-radar.js:395.
// A team spoke says which way is better itself (live/team-radar.ts); a player's is looked up.
export function ensureChartFrame(svg: SVGSVGElement, axes: readonly (RadarAxisWithRange & { lowerBetter?: boolean })[]): void {
  const signature = axes.map((a) => a.key).join("|");
  if (svg.dataset.axisSignature === signature) return;
  svg.dataset.axisSignature = signature;

  const n = axes.length;
  const ringsG = svg.querySelector<SVGGElement>(".radar-rings")!;
  const spokesG = svg.querySelector<SVGGElement>(".radar-spokes")!;
  const bandedFillG = svg.querySelector<SVGGElement>(".radar-banded-fill")!;
  const wedgesG = svg.querySelector<SVGGElement>(".radar-wedges")!;
  const labelsOverlay = svg.parentElement!.querySelector<HTMLElement>(".radar-labels-overlay")!;
  ringsG.innerHTML = "";
  spokesG.innerHTML = "";
  bandedFillG.innerHTML = "";
  wedgesG.innerHTML = "";
  labelsOverlay.innerHTML = "";
  if (!n) return;

  bandedFillG.setAttribute("clip-path", `url(#${ensureClipId(svg)})`);

  // Rings are true circles (not axis-point polygons) — the band edges curve smoothly. Two copies
  // per band: the neutral grid ring, and a green ring clipped to the live data shape (the solo
  // banded fill). Largest-to-smallest so each smaller filled band layers on top. rrjs:419.
  RING_BAND_FRACS.forEach((frac, bandIdx) => {
    const r = (CHART_RADIUS * frac).toFixed(2);
    const bandClass = bandIdx % 2 === 0 ? "band-a" : "band-b";

    const circle = document.createElementNS(SVG_NS, "circle");
    circle.setAttribute("class", "radar-ring-band " + bandClass);
    circle.setAttribute("cx", String(CHART_CENTER));
    circle.setAttribute("cy", String(CHART_CENTER));
    circle.setAttribute("r", r);
    ringsG.appendChild(circle);

    const bandedCircle = document.createElementNS(SVG_NS, "circle");
    bandedCircle.setAttribute("class", "radar-banded-ring " + bandClass);
    bandedCircle.setAttribute("cx", String(CHART_CENTER));
    bandedCircle.setAttribute("cy", String(CHART_CENTER));
    bandedCircle.setAttribute("r", r);
    bandedFillG.appendChild(bandedCircle);
  });

  axes.forEach((axis, i) => {
    const angle = axisAngle(i, n);
    const outer = axisPoint(angle, 1);
    const spoke = document.createElementNS(SVG_NS, "line");
    spoke.setAttribute("class", "radar-spoke");
    spoke.setAttribute("x1", String(CHART_CENTER));
    spoke.setAttribute("y1", String(CHART_CENTER));
    spoke.setAttribute("x2", outer.x.toFixed(2));
    spoke.setAttribute("y2", outer.y.toFixed(2));
    spokesG.appendChild(spoke);

    const wedge = document.createElementNS(SVG_NS, "path");
    wedge.setAttribute("class", "radar-wedge");
    wedge.setAttribute("data-axis-key", axis.key);
    wedge.setAttribute("d", wedgePath(angle, n));
    wedge.addEventListener("mouseenter", () => setAxisTicksActive(svg, axis.key, true));
    wedge.addEventListener("mouseleave", () => setAxisTicksActive(svg, axis.key, false));
    wedgesG.appendChild(wedge);

    // Axis label — plain HTML, positioned by % over the chart (see the skeleton comment).
    const labelPt = axisPoint(angle, 1.1);
    const label = document.createElement("span");
    label.className = "radar-axis-label";
    // Which stat it is — a live Radar lets you drag a label out to rank everyone on it (RadarModule).
    label.dataset.axisKey = axis.key;
    label.style.left = ((labelPt.x / CHART_SIZE) * 100).toFixed(2) + "%";
    label.style.top = ((labelPt.y / CHART_SIZE) * 100).toFixed(2) + "%";
    label.style.transform = `${labelTransformPct(angle)} rotate(${labelRotationDeg(angle).toFixed(1)}deg)`;
    label.textContent = truncateLabel(axis.label, AXIS_LABEL_MAX_LEN);
    if (axis.label.length > AXIS_LABEL_MAX_LEN) label.dataset.tooltip = axis.label;
    labelsOverlay.appendChild(label);

    // Per-axis scale printed on the chart — a lower-is-better axis runs max-at-centre down to
    // min-at-edge so the ring a genuinely-good low value plots on always shows the real value
    // that belongs there (matches metricFraction's own inversion). renderers-radar.js:488.
    if (axis.min !== undefined) {
      const lowerBetter = axis.lowerBetter ?? isLowerBetter(axis.key);
      TICK_FRACS.forEach((frac) => {
        const tickValue = lowerBetter
          ? axis.max - frac * (axis.max - axis.min)
          : axis.min + frac * (axis.max - axis.min);
        const tickPt = axisPoint(angle, frac);
        const tick = document.createElement("span");
        tick.className = "radar-tick-label";
        tick.dataset.axisKey = axis.key;
        tick.style.left = ((tickPt.x / CHART_SIZE) * 100).toFixed(2) + "%";
        tick.style.top = ((tickPt.y / CHART_SIZE) * 100).toFixed(2) + "%";
        tick.style.transform = labelTransformPct(angle);
        tick.textContent = formatMetricValue(tickValue, axis.outputType);
        labelsOverlay.appendChild(tick);
      });
    }
  });
}

// Draws/updates one polygon per player (d3 join, keyed by colour letter, with the legacy's
// 400/450/250ms enter/update/exit transitions). Solo mode's fill is the green banded rings
// clipped to the data shape, so the shared clip polygon tracks the single entry's own points;
// overlay mode (2-5 players, 04h-5) collapses it. renderers-radar.js:515. Generic over entries
// so 04h-5's overlay reuses it unchanged; 04h-4b only ever passes one.
export function updateRadarData(
  svg: SVGSVGElement,
  axes: readonly RadarAxis[],
  entries: readonly RadarEntry[],
): void {
  const polyG = d3.select(svg.querySelector<SVGGElement>(".radar-polygons")!);
  // Solo gets a denser fill (its own .solo modifier) — legible because there's just one shape.
  const soloClass = entries.length === 1 ? " solo" : "";

  polyG
    .selectAll("polygon.radar-polygon")
    .data(entries as RadarEntry[], (d) => (d as RadarEntry).colorLetter)
    .join(
      (enter) =>
        enter
          .append("polygon")
          .attr("class", (d) => `radar-polygon marker-${d.colorLetter}${soloClass}`)
          .attr("points", (d) => polygonPoints(axes, d.fractions))
          .style("opacity", 0)
          .call((sel) => sel.transition().duration(400).style("opacity", 1)),
      (update) =>
        update
          .attr("class", (d) => `radar-polygon marker-${d.colorLetter}${soloClass}`)
          .call((sel) =>
            sel
              .transition()
              .duration(450)
              .ease(d3.easeCubicOut)
              .attr("points", (d) => polygonPoints(axes, d.fractions))
              // A new transition cancels an unfinished one: a redraw right after a shape appeared
              // (two quick updates) left its fade-in stuck near opacity 0, so with two players
              // both shapes vanished (Ben, 2026-10-16, Similarity overlay). Always fade back in.
              .style("opacity", 1),
          ),
      (exit) => exit.transition().duration(250).style("opacity", 0).remove(),
    );

  // The solo banded fill's clip shape has to track the exact same points as the visible
  // polygon, animated the same way, or the two drift apart mid-transition. rrjs:545.
  const clipPolygon = d3.select(svg.querySelector<SVGPolygonElement>(".radar-clip-polygon")!);
  if (entries.length === 1) {
    clipPolygon
      .transition()
      .duration(450)
      .ease(d3.easeCubicOut)
      .attr("points", polygonPoints(axes, entries[0]!.fractions));
  } else {
    clipPolygon.interrupt().attr("points", "");
  }

  updateWedgeTooltips(svg, axes, entries);
}

// One tooltip per axis (not per player-per-axis) — combines every entry's value for that axis,
// so an overlay wedge shows all of them at once. renderers-radar.js:561.
function updateWedgeTooltips(svg: SVGSVGElement, axes: readonly RadarAxis[], entries: readonly RadarEntry[]): void {
  const wedgesG = svg.querySelector<SVGGElement>(".radar-wedges");
  if (!wedgesG) return;
  Array.from(wedgesG.children).forEach((wedge, i) => {
    const axis = axes[i];
    if (!axis) return;
    const lines = entries
      .map((entry) => (entry.values[i] == null ? null : `${entry.tooltipPrefix}: ${formatMetricValue(entry.values[i], axis.outputType)}`))
      .filter(Boolean) as string[];
    if (lines.length) (wedge as HTMLElement).dataset.tooltip = `${axis.label}\n${lines.join("\n")}`;
    else delete (wedge as HTMLElement).dataset.tooltip;
  });
}

// Stop any in-flight d3 transitions on the chart before React tears the host down — the island's
// useEffect cleanup calls this (the D3-ResizeObserver-cleanup discipline; the single-player chart
// has no ResizeObserver of its own — the table's lands in 04h-4c). renderers-radar.js has no
// explicit teardown because main.js just drops the DOM; React needs the interrupt to be clean.
export function teardownRadarChart(svg: SVGSVGElement): void {
  d3.select(svg.querySelector<SVGGElement>(".radar-polygons")!).selectAll("polygon.radar-polygon").interrupt();
  const clip = svg.querySelector<SVGPolygonElement>(".radar-clip-polygon");
  if (clip) d3.select(clip).interrupt();
}
